import { Select, Skeleton } from 'antd'
import { flattenWorkflowOntologyFields } from 'features/libra/ontology/flattenOntologyFields'
import {
  type LibraTicket,
  parseLibraTicketEntry,
} from 'features/libra/ticket/libraTicketsApi'
import { mapEntryToLibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useRolodexEntries } from 'features/rolodex'
import isNil from 'lodash/isNil'
import type { FC } from 'react'
import { useDeferredValue, useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

import { computeTicketLlmStats } from './computeTicketLlmStats'
import { PerFieldTable } from './PerFieldTable'
import { PerTicketTable } from './PerTicketTable'
import { SummaryCards } from './SummaryCards'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Props = {
  orgId: string
  groupId?: string
}

const TicketLlmStatsTab: FC<Props> = ({ orgId, groupId }) => {
  // ── Tickets ──────────────────────────────────────────────────────────────
  // Scope matches InternalGeckoStatsDrawer: filter by the active group when
  // available, otherwise fall back to the whole organization.
  const { entries: ticketEntries, isLoading: ticketsLoading } =
    useRolodexEntries(
      useMemo(
        () => ({
          type: RolodexEntryType.LIBRA_TICKET,
          tags: groupId
            ? ({ [RolodexEntryType.LIBRA_TICKET_GROUP]: [groupId] } as Record<
                string,
                string[]
              >)
            : { organization: orgId },
        }),
        [orgId, groupId]
      )
    )

  const tickets = useMemo<ReadonlyArray<LibraTicket>>(() => {
    const out: LibraTicket[] = []
    for (const entry of ticketEntries ?? []) {
      const ticket = parseLibraTicketEntry(entry)
      if (ticket) out.push(ticket)
    }
    return out
  }, [ticketEntries])

  // ── Workflow entries for field-name mapping ─────────────────────────────
  // Collect unique workflowIds referenced by the tickets so we can look up
  // human-readable field names without needing a WorkflowProvider in the tree.
  const workflowIds = useMemo(() => {
    const ids = new Set<string>()
    for (const t of tickets) {
      if (t.workflowId) ids.add(t.workflowId)
    }
    // Sort for stable query-key identity across re-renders.
    return Array.from(ids).sort()
  }, [tickets])

  const { entries: workflowEntries } = useRolodexEntries(
    useMemo(
      () =>
        workflowIds.length > 0
          ? { type: RolodexEntryType.LIBRA_WORKFLOW, ids: workflowIds }
          : undefined,
      [workflowIds]
    )
  )

  // Walk every workflow's ontologyFields across BOTH scopes (ticket-level
  // and the reserved `components` subtree) and merge into a single map, so
  // component-scoped fields resolve display names too. Keep the first name
  // seen for determinism when the same fieldId appears under different
  // names across workflows.
  const fieldIdToName = useMemo(() => {
    const map = new Map<string, string>()
    for (const entry of workflowEntries ?? []) {
      const workflow = mapEntryToLibraWorkflow(entry)
      const flattened = flattenWorkflowOntologyFields(
        workflow.globalContext.ontologyFields
      )
      for (const { fieldId, definition } of flattened) {
        if (map.has(fieldId)) continue
        if (typeof definition.name === 'string' && definition.name) {
          map.set(fieldId, definition.name)
        }
      }
    }
    return map
  }, [workflowEntries])

  // ── Site filter ──────────────────────────────────────────────────────────
  const siteOptions = useMemo(() => {
    const set = new Set<string>()
    for (const t of tickets) {
      if (!isNil(t.tags.site)) set.add(t.tags.site)
    }
    return Array.from(set).sort()
  }, [tickets])

  const [siteFilter, setSiteFilter] = useState<string | undefined>(undefined)
  const deferredSiteFilter = useDeferredValue(siteFilter)

  // ── Compute stats ────────────────────────────────────────────────────────
  const stats = useMemo(
    () =>
      computeTicketLlmStats(tickets, fieldIdToName, {
        siteFilter: deferredSiteFilter,
      }),
    [tickets, fieldIdToName, deferredSiteFilter]
  )

  const aiTouched = useMemo(
    () => stats.perTicket.filter((t) => t.hasLlmLineage),
    [stats.perTicket]
  )

  const selectOptions = useMemo(
    () => siteOptions.map((s) => ({ value: s, label: s })),
    [siteOptions]
  )

  if (ticketsLoading) return <Skeleton active paragraph={{ rows: 3 }} />

  return (
    <Wrapper>
      <FilterRow>
        <FilterLabel>Site:</FilterLabel>
        <Select<string | undefined>
          placeholder='All sites'
          value={siteFilter}
          onChange={setSiteFilter}
          allowClear
          style={{ minWidth: 220 }}
          options={selectOptions}
        />
      </FilterRow>

      <SummaryCards stats={stats} />

      <SectionTitle>Per-Ticket Breakdown</SectionTitle>
      <PerTicketTable tickets={aiTouched} />

      <SectionTitle>Per-Field Breakdown</SectionTitle>
      <PerFieldTable fields={stats.perField} />
    </Wrapper>
  )
}

export default TicketLlmStatsTab

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`

const FilterLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  white-space: nowrap;
`

const SectionTitle = styled.h3`
  margin: 1rem 0 0.75rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`
